/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ComponentType, useCallback, useEffect, useMemo, useState } from 'react';

import { jsx } from '@compiled/react';

import { type NewCoreIconProps } from '@atlaskit/icon';
import { Ati } from '@atlassian/ari';
import type { CrossAppItemResponse } from '@atlassian/navigation-bff-data-provider/universal-create';

import { iconLoader } from '../../../services';
import type { AllowedIcon, MenuItem } from '../../../types';
import type { CreationContext } from '../../../types/crossAppCreation';
import MenuContentItem from '../MenuItem';

export interface CrossAppItemBaseProps {
	/**
	 * Additional context for the component
	 */
	context: CreationContext;
	/**
	 * Optional configuration to override the default menu item properties
	 * Note: isLoading will be combined with the internal icon loading state
	 */
	customisedItemConfig?: Partial<MenuItem>;
	/**
	 * Optional analytics injection callbacks for click tracking
	 */
	injectClickAnalytics?: {
		onClick?: (isRightClick: boolean) => void;
	};
	/**
	 * The response item from Universal Create
	 */
	responseItem: CrossAppItemResponse;
}

const noop = () => {};

/**
 * Cross-app item base component that renders a cross-app item in the Universal Create interface.
 * Handles dynamic icon loading and loading states.
 * Note: This component does not include analytics. Analytics will be added in a future task.
 */
export function CrossAppItemBase({
	responseItem,
	customisedItemConfig,
	context,
	injectClickAnalytics,
}: CrossAppItemBaseProps) {
	const onClick = useCallback(() => {
		const onItemClick = context?.onItemClick || noop;

		// Call injected analytics for left click
		injectClickAnalytics?.onClick?.(false);

		// Call onItemClick callback if provided
		onItemClick?.(responseItem.ati);
	}, [context, responseItem, injectClickAnalytics]);

	const [icon, setIcon] = useState<ComponentType<NewCoreIconProps> | undefined>(undefined);
	const [isIconLoading, setIsIconLoading] = useState(false);
	const { isLoading: isCustomisedItemConfigLoading, ...restCustomisedItemConfig } =
		customisedItemConfig || {};

	const onRightClick = useCallback(
		(event: React.MouseEvent) => {
			// Call injected analytics for right click
			injectClickAnalytics?.onClick?.(true);

			// Call custom onContextMenu handler if provided
			const customOnContextMenu = restCustomisedItemConfig?.onContextMenu;
			customOnContextMenu?.(event);
		},
		[restCustomisedItemConfig?.onContextMenu, injectClickAnalytics],
	);

	useEffect(
		function loadIcon() {
			if (!responseItem.icon) {
				setIcon(undefined);
				setIsIconLoading(false);
				return;
			}

			setIsIconLoading(true);

			iconLoader(responseItem.icon as AllowedIcon).then((loadedIcon) => {
				if (loadedIcon) {
					setIcon(() => loadedIcon);
				} else {
					setIcon(undefined);
				}
				setIsIconLoading(false);
			});
		},
		[responseItem.icon],
	);

	// Extract custom onClick handler and compose with onClick
	const customOnClick = restCustomisedItemConfig?.onClick;

	const composedOnClick = useCallback(
		(event: React.MouseEvent | React.KeyboardEvent) => {
			// Call onClick handler first
			onClick();
			// Then call custom click handler if provided
			customOnClick?.(event);
		},
		[customOnClick, onClick],
	);

	const menuItem: MenuItem = useMemo(() => {
		const { onClick: _, onContextMenu: __, ...otherCustomConfig } = restCustomisedItemConfig || {};

		return {
			ati: Ati.parse(responseItem.ati),
			children: responseItem.name,
			elemBefore: icon,
			isLoading: isIconLoading || isCustomisedItemConfigLoading,
			testId: `cross-app-item-${responseItem.ati}`,
			onClick: customOnClick ? composedOnClick : onClick,
			onContextMenu: onRightClick,
			...otherCustomConfig,
		};
	}, [
		responseItem.ati,
		responseItem.name,
		icon,
		isIconLoading,
		isCustomisedItemConfigLoading,
		onClick,
		composedOnClick,
		customOnClick,
		restCustomisedItemConfig,
		onRightClick,
	]);

	return <MenuContentItem item={menuItem} />;
}
